import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Send, Check } from 'lucide-react';
interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  inquiryType: string;
}
export function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: 'general'
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const inquiryTypes = [{
    value: 'general',
    label: 'General Inquiry'
  }, {
    value: 'reservation',
    label: 'Table Reservation'
  }, {
    value: 'bottle-service',
    label: 'Bottle Service'
  }, {
    value: 'private-event',
    label: 'Private Event'
  }, {
    value: 'corporate',
    label: 'Corporate Event'
  }, {
    value: 'careers',
    label: 'Careers'
  }];
  const validateForm = () => {
    const newErrors: Partial<FormData> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Simulate form submission
      setTimeout(() => {
        setIsSubmitted(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: '',
            inquiryType: 'general'
          });
        }, 3000);
      }, 500);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  if (isSubmitted) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="flex flex-col items-center justify-center h-full min-h-[600px] text-center p-8" data-id="element-128">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        delay: 0.2,
        type: 'spring',
        stiffness: 200
      }} className="w-20 h-20 bg-[#DDA247] rounded-full flex items-center justify-center mb-6" data-id="element-129">
          <Check className="w-10 h-10 text-[#0B1014]" data-id="element-130" />
        </motion.div>
        <h3 className="text-2xl font-bold text-white mb-3" data-id="element-131">Message Sent!</h3>
        <p className="text-gray-400 max-w-md" data-id="element-132">
          Thank you for reaching out. We'll get back to you within 24 hours.
        </p>
      </motion.div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-133">
      {/* Inquiry Type */}
      <div data-id="element-134">
        <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-3" data-id="element-135">
          Inquiry Type
        </label>
        <select name="inquiryType" value={formData.inquiryType} onChange={handleChange} className="w-full bg-[#1A2228] border border-white/10 rounded-xl px-4 py-3.5 text-white focus:border-[#DDA247] focus:outline-none transition-colors" data-id="element-136">
          {inquiryTypes.map(type => <option key={type.value} value={type.value} data-id="element-137">
              {type.label}
            </option>)}
        </select>
      </div>

      {/* Name & Email Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-138">
        <div data-id="element-139">
          <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-3" data-id="element-140">
            Full Name *
          </label>
          <input type="text" name="name" value={formData.name} onChange={handleChange} className={`w-full bg-[#1A2228] border rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none transition-colors ${errors.name ? 'border-red-500' : 'border-white/10 focus:border-[#DDA247]'}`} placeholder="John Doe" data-id="element-141" />
          {errors.name && <motion.p initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-red-400 text-xs mt-2" data-id="element-142">
              {errors.name}
            </motion.p>}
        </div>

        <div data-id="element-143">
          <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-3" data-id="element-144">
            Email Address *
          </label>
          <input type="email" name="email" value={formData.email} onChange={handleChange} className={`w-full bg-[#1A2228] border rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none transition-colors ${errors.email ? 'border-red-500' : 'border-white/10 focus:border-[#DDA247]'}`} placeholder="john@example.com" data-id="element-145" />
          {errors.email && <motion.p initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-red-400 text-xs mt-2" data-id="element-146">
              {errors.email}
            </motion.p>}
        </div>
      </div>

      {/* Phone & Subject Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-147">
        <div data-id="element-148">
          <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-3" data-id="element-149">
            Phone Number *
          </label>
          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={`w-full bg-[#1A2228] border rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none transition-colors ${errors.phone ? 'border-red-500' : 'border-white/10 focus:border-[#DDA247]'}`} placeholder="(301) 555-0100" data-id="element-150" />
          {errors.phone && <motion.p initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-red-400 text-xs mt-2" data-id="element-151">
              {errors.phone}
            </motion.p>}
        </div>

        <div data-id="element-152">
          <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-3" data-id="element-153">
            Subject *
          </label>
          <input type="text" name="subject" value={formData.subject} onChange={handleChange} className={`w-full bg-[#1A2228] border rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none transition-colors ${errors.subject ? 'border-red-500' : 'border-white/10 focus:border-[#DDA247]'}`} placeholder="How can we help?" data-id="element-154" />
          {errors.subject && <motion.p initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-red-400 text-xs mt-2" data-id="element-155">
              {errors.subject}
            </motion.p>}
        </div>
      </div>

      {/* Message */}
      <div data-id="element-156">
        <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-3" data-id="element-157">
          Message *
        </label>
        <textarea name="message" value={formData.message} onChange={handleChange} rows={6} className={`w-full bg-[#1A2228] border rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none transition-colors resize-none ${errors.message ? 'border-red-500' : 'border-white/10 focus:border-[#DDA247]'}`} placeholder="Tell us more about your inquiry..." data-id="element-158" />
        {errors.message && <motion.p initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-red-400 text-xs mt-2" data-id="element-159">
            {errors.message}
          </motion.p>}
      </div>

      {/* Submit Button */}
      <Button type="submit" variant="primary" size="lg" fullWidth className="group" data-id="element-160">
        <span className="flex items-center justify-center gap-2" data-id="element-161">
          Send Message
          <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-162" />
        </span>
      </Button>

      <p className="text-xs text-gray-600 text-center" data-id="element-163">
        We typically respond within 24 hours during business days
      </p>
    </form>;
}